import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/api';
import { FaUsers, FaChartLine, FaCog, FaBell, FaSignOutAlt, FaEye, FaTimes, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import './admindashboard.css';

const AdminDashboard = () => {
  const { user, logout, refreshAccessToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('clients');
  const [companies, setCompanies] = useState([]);
  const [allPreScreens, setAllPreScreens] = useState([]);
  const [loading, setLoading] = useState({
    initialData: false
  });
  const [error, setError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [expandedCompanyIds, setExpandedCompanyIds] = useState([]);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [isAdminLoaded, setIsAdminLoaded] = useState(false);

  const getValidToken = useCallback(async () => {
    try {
      if (!api.defaults.headers.common['Authorization']) {
        await refreshAccessToken();
      }
      return api.defaults.headers.common['Authorization'];
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }, [refreshAccessToken]);

  const fetchInitialData = useCallback(async () => {
    setLoading(prev => ({ ...prev, initialData: true }));
    setError(null);
    try {
      await getValidToken();

      const [companiesResponse, preScreensResponse] = await Promise.all([
        api.get('/server/companies/getCompanyListExceptAdmin'),
        api.get('/preScreen/getQuestions')
      ]);

      const enrichedCompanies = companiesResponse.data.map(company => {
        const enrichedCandidates = company.candidates.map(candidate => {
          const matchingPreScreen = preScreensResponse.data.find(
            ps => ps.email === candidate.email
          );
          return {
            ...candidate,
            preScreenQuestions: matchingPreScreen?.questions || [],
            preScreenResumeBase64: matchingPreScreen?.resumeBase64 || null,
            preScreenStatus: matchingPreScreen?.status || 'N/A',
          };
        });
        return {
          ...company,
          candidates: enrichedCandidates
        };
      });

      setCompanies(enrichedCompanies);
      setAllPreScreens(preScreensResponse.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch data');
      console.error('Error fetching initial data:', err);
    } finally {
      setLoading(prev => ({ ...prev, initialData: false }));
    }
  }, [getValidToken]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
    } else if (user.role !== 'admin') {
      return;
    } else {
      setIsAdminLoaded(true);
      const tabFromUrl = location.pathname.split('/')[2] || 'clients';
      setActiveTab(tabFromUrl);
      if (companies.length === 0 && !loading.initialData) {
        fetchInitialData();
      }
    }
  }, [user, navigate, authLoading, location.pathname, companies.length, loading.initialData, fetchInitialData]);

  const toggleCompanyExpansion = (companyId) => {
    setExpandedCompanyIds(prev =>
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const handleTabClick = (tab, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setActiveTab(tab);
    setError(null);
    navigate(`/admin/${tab}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const viewCompanyDetails = (company) => {
    setSelectedCompany(company);
    setShowCompanyModal(true);
  };

  const CompanyDetailsModal = () => (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close" onClick={() => setShowCompanyModal(false)}>
          <FaTimes />
        </button>
        <h3>{selectedCompany.name}</h3>
        <div className="modal-details">
          <p><strong>Email:</strong> {selectedCompany.email}</p>
          <p><strong>ID:</strong> {selectedCompany.id}</p>
          <p><strong>Candidates:</strong> {selectedCompany.candidates?.length || 0}</p>
        </div>
      </div>
    </div>
  );

  const renderCompanyRow = (company) => {
    const isExpanded = expandedCompanyIds.includes(company.id);
    const companyCandidates = company.candidates || [];

    return (
      <React.Fragment key={company.id}>
        <tr className="company-row">
          <td>
            <button
              className="expand-toggle"
              onClick={() => toggleCompanyExpansion(company.id)}
              aria-label={isExpanded ? 'Collapse company' : 'Expand company'}
            >
              {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
            </button>
            <span className="company-name">{company.name}</span>
            <span className="candidate-count-badge">{company.candidateCount || companyCandidates.length}</span>
          </td>
          <td>{company.email}</td>
          <td className="actions-cell">
            <button
              className="action-button view-button"
              onClick={() => viewCompanyDetails(company)}
              aria-label={`View details for ${company.name}`}
            >
              <FaEye /> View
            </button>
          </td>
        </tr>

        {isExpanded && (
          <tr className="candidates-row">
            <td colSpan="3">
              <div className="candidates-container">
                {companyCandidates.length === 0 ? (
                  <div className="empty-state">No candidates found for this company</div>
                ) : (
                  <table className="candidates-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Questions</th>
                        <th>Resume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyCandidates.map((candidate) => (
                        <tr key={candidate.id}>
                          <td className="candidate-email">{candidate.email}</td>
                          <td>
                            <span className={`status-badge ${(candidate.preScreenStatus || 'n/a').toLowerCase().replace(/\s+/g, '-')}`}>
                              {candidate.preScreenStatus || 'N/A'}
                            </span>
                          </td>
                          <td>
                            {candidate.preScreenQuestions?.length > 0 ? (
                              <ul className="questions-list">
                                {candidate.preScreenQuestions.map((q, i) => (
                                  <li key={i}>{q}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="no-questions">No questions</span>
                            )}
                          </td>
                          <td>
                            {candidate.preScreenResumeBase64 ? (
                              <a
                                href={`data:application/pdf;base64,${candidate.preScreenResumeBase64}`}
                                download={`resume_${candidate.email}.pdf`}
                                className="download-link"
                                aria-label={`Download resume for ${candidate.email}`}
                              >
                                Download
                              </a>
                            ) : (
                              <span className="no-resume">No resume</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'clients':
        return (
          <div className="clients-section">
            <div className="section-header">
              <h2>Company Information</h2>
              <div className="header-controls">
                <button
                  className="refresh-button"
                  onClick={fetchInitialData}
                  disabled={loading.initialData}
                  aria-label="Refresh data"
                >
                  {loading.initialData ? (
                    <span className="refresh-spinner"></span>
                  ) : (
                    'Refresh Data'
                  )}
                </button>
              </div>
            </div>

            {loading.initialData ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading company data...</p>
              </div>
            ) : error ? (
              <div className="error-message">
                <p>{error}</p>
                <button onClick={fetchInitialData} className="retry-button">
                  Retry
                </button>
              </div>
            ) : companies.length === 0 ? (
              <div className="empty-state">
                <p>No companies found</p>
                <button onClick={fetchInitialData} className="retry-button">
                  Try Again
                </button>
              </div>
            ) : (
              <div className="table-container">
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Email</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.map(renderCompanyRow)}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      
      default:
        return (
          <div className="default-section">
            <h2>Admin Dashboard</h2>
            <p>Select a section from the sidebar to get started</p>
          </div>
        );
    }
  };

  if (!isAdminLoaded) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h2>Admin Panel</h2>
          <div className="user-info">
            <span className="user-name">{user?.userName}</span>
            <span className="user-role">Administrator</span>
          </div>
        </div>
        
        <div className="admin-sidebar-nav">
  <div className="nav-group">
    <button
      className={`nav-button ${activeTab === 'clients' ? 'active' : ''}`}
      onClick={(e) => handleTabClick('clients', e)}
    >
      <FaUsers className="icon" />
      <span>Companies</span>
    </button>
    
    <button className="nav-button">
      <FaChartLine className="icon" />
      <span>Analytics</span>
    </button>

    <button className="nav-button">
      <FaCog className="icon" />
      <span>Settings</span>
    </button>
  </div>
  
  <div className="nav-group">
    <button 
      className="nav-button logout-button"
      onClick={handleLogout}
    >
      <FaSignOutAlt className="icon" />
      <span>Logout</span>
    </button>
  </div>
</div>
</div>

      <div className="admin-main-content">
        <div className="admin-main-header">
          <div className="header-title">
            <h1>
              {activeTab === 'clients' ? 'Company Management' : 'Admin Dashboard'}
            </h1>
            <p className="header-subtitle">
              {activeTab === 'clients' ? 'View and manage company accounts' : 'System overview'}
            </p>
          </div>
          
          <div className="header-actions">
  <button className="notification-button" aria-label="Notifications">
    <FaBell />
  </button>
</div>
        </div>

        <div className="content-container">
          {renderContent()}
        </div>
        
       
      </div>

      {showCompanyModal && <CompanyDetailsModal />}
    </div>
  );
};

export default AdminDashboard;